import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import type { ApiError } from '@types';

/**
 * IEBC dashboard API (/api/iebc/*, served by its own backend process).
 *
 * IEBC sessions are separate from SDICS ones: own tokens, own storage keys
 * and own refresh endpoint — so on sdics.tech/iebc an IEBC sign-in never
 * touches an administrator's SDICS session in the same browser, and the
 * shared apiClient (with its /login redirects) is not used here.
 */

const ACCESS_KEY = 'iebc_access_token';
const REFRESH_KEY = 'iebc_refresh_token';
const USER_KEY = 'iebc_user';

export interface IEBCUser {
  id: number;
  username: string;
  full_name: string;
}

/** One area an IEBC account may view (lower levels null = the whole level). */
export interface IEBCArea {
  county: string;
  district: string | null;
  division: string | null;
  location: string | null;
}

export interface IEBCMe extends IEBCUser {
  areas: IEBCArea[];
  unregistered_total: number;
}

/** One unregistered voter, exactly the fields IEBC is shown. */
export interface IEBCVoter {
  national_id: string;
  full_name: string;
  father_name: string | null;
  /** NRB clan, shown as "Clan name". */
  family_name: string | null;
  county: string;
  district: string;
  division: string;
  location: string;
  sub_location: string | null;
  village: string | null;
}

export interface IEBCVoterPage {
  count: number;
  next: string | null;
  previous: string | null;
  results: IEBCVoter[];
}

/** Area filter levels, top to bottom, as the API names them. */
export const IEBC_LEVELS = ['county', 'district', 'division', 'location', 'sub_location', 'village'] as const;
export type IEBCLevel = (typeof IEBC_LEVELS)[number];
export type IEBCFilters = Partial<Record<IEBCLevel, string>>;

const store = {
  get: (k: string) => {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  },
  set: (k: string, v: string) => {
    try {
      localStorage.setItem(k, v);
    } catch {
      /* private mode: the session lasts until the page closes */
    }
  },
  clear: () => {
    try {
      [ACCESS_KEY, REFRESH_KEY, USER_KEY].forEach((k) => localStorage.removeItem(k));
    } catch {
      /* nothing stored */
    }
  },
};

const http = axios.create({
  baseURL: `${import.meta.env.VITE_API_URL || '/api'}/iebc/`,
  timeout: 60000,
  headers: { 'Content-Type': 'application/json' },
});

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = store.get(ACCESS_KEY);
  if (token && !config.url?.startsWith('login/') && !config.url?.startsWith('refresh/')) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// An expired access token is refreshed once; if the session itself has
// ended (signed in elsewhere, signed out, deactivated) the stored session
// is cleared and the page sends the user to sign in.
let refreshing: Promise<string> | null = null;
http.interceptors.response.use(undefined, async (error: AxiosError) => {
  const req = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
  const url = req?.url || '';
  if (error.response?.status !== 401 || req._retry || url.startsWith('login/') || url.startsWith('refresh/')) {
    if (error.response?.status === 401 && !url.startsWith('login/')) store.clear();
    return Promise.reject(error);
  }
  req._retry = true;
  try {
    const refresh = store.get(REFRESH_KEY);
    if (!refresh) throw error;
    refreshing ??= http
      .post<{ access_token: string }>('refresh/', { refresh })
      .then((r) => r.data.access_token)
      .finally(() => {
        refreshing = null;
      });
    const access = await refreshing;
    store.set(ACCESS_KEY, access);
    req.headers.Authorization = `Bearer ${access}`;
    return http(req);
  } catch (e) {
    store.clear();
    return Promise.reject(e);
  }
});

/** API errors in the SDICS shape: { error: { code, message } }. */
const asApiError = (e: unknown): ApiError => {
  if (axios.isAxiosError(e)) {
    const data = e.response?.data as ApiError | undefined;
    if (data?.error) return data;
    return { error: { code: e.response ? String(e.response.status) : 'NETWORK', message: e.message } };
  }
  return { error: { code: 'UNKNOWN', message: e instanceof Error ? e.message : 'Unknown error' } };
};

const get = async <T,>(url: string): Promise<T> => {
  try {
    return (await http.get<T>(url)).data;
  } catch (e) {
    throw asApiError(e);
  }
};

const query = (params: Record<string, string | number | undefined>) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '') q.append(k, String(v));
  });
  return q.toString();
};

export const iebcApi = {
  /** Signed in (as far as this browser knows)? */
  hasSession: () => !!store.get(REFRESH_KEY),

  /** Phone number (any common form) + password. */
  login: async (username: string, password: string): Promise<IEBCUser> => {
    try {
      const { data } = await http.post<{ access_token: string; refresh_token: string; user: IEBCUser }>(
        'login/',
        { username, password },
      );
      store.set(ACCESS_KEY, data.access_token);
      store.set(REFRESH_KEY, data.refresh_token);
      store.set(USER_KEY, JSON.stringify(data.user));
      return data.user;
    } catch (e) {
      throw asApiError(e);
    }
  },

  /** Ends the session on the server too; the password is kept. */
  logout: async () => {
    try {
      await http.post('logout/');
    } catch {
      /* already ended */
    } finally {
      store.clear();
    }
  },

  me: () => get<IEBCMe>('me/'),

  /** Values one level holds inside the account's areas, under the parents chosen. */
  areas: async (level: IEBCLevel, parents: IEBCFilters) =>
    (await get<{ level: string; values: string[] }>(`areas/?${query({ level, ...parents })}`)).values,

  /** The best few matches for what has been typed, for the search suggestions. */
  suggest: async (q: string) =>
    (await get<{ results: IEBCVoter[] }>(`suggest/?${query({ q: q.trim() })}`)).results,

  voters: (filters: IEBCFilters, q: string, page: number, pageSize: number) =>
    get<IEBCVoterPage>(`voters/?${query({ ...filters, q: q.trim(), page, page_size: pageSize })}`),
};
